import type { ReactNode } from "react";
import { Inbox } from "lucide-react";
import { cn } from "@/lib/cn";

export interface EmptyStateProps {
  title: string;
  /** A helpful next step or explanation — never leave the user staring at a blank box. */
  body?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/** Empty state for any async surface that returned no rows. Always pair with a helpful body. */
export function EmptyState({ title, body, icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-14 text-center",
        className,
      )}
    >
      <span
        className="flex size-12 items-center justify-center rounded-full bg-(--surface-sunken) text-(--text-subtle) ring-1 ring-inset ring-(--border-hairline)"
        aria-hidden
      >
        {icon ?? <Inbox className="size-6" />}
      </span>
      <div className="flex max-w-sm flex-col gap-1">
        <p className="text-(length:--text-sm) font-semibold text-(--text-strong)">{title}</p>
        {body && (
          <p className="text-(length:--text-xs) leading-relaxed text-(--text-muted)">{body}</p>
        )}
      </div>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
